import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, Euro, Activity, Plus, Trash2, Check, Sparkles, Loader2, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import MyDataSettings from '../components/MyDataSettings';
import EmailSettings from '../components/EmailSettings';
import { UserOverviewModal, FinancePanel, UsagePanel } from '../components/AdminInsights';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return String(v); }
};

const PLAN_LABEL = { free: 'Δωρεάν', limited: 'Limited €10', unlimited: 'Unlimited €15', beta: 'Beta Tester ∞' };

export default function AdminPanel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notAdmin, setNotAdmin] = useState(false);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [working, setWorking] = useState(null); // user_id currently being updated
  const [overviewUserId, setOverviewUserId] = useState(null);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [u, s] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      setUsers(u.data?.users || []);
      setStats(s.data || null);
    } catch (err) {
      if (err.response?.status === 403) setNotAdmin(true);
      else toast.error('Αποτυχία φόρτωσης');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const activatePlan = async (userId, plan, months = 1) => {
    setWorking(userId);
    try {
      await axios.patch(`${API}/admin/users/${userId}/subscription`, { plan, months }, { withCredentials: true });
      toast.success(`Ενεργοποιήθηκε ${PLAN_LABEL[plan]}`);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Σφάλμα');
    } finally {
      setWorking(null);
    }
  };

  const addCredits = async (userId) => {
    const amountStr = window.prompt('Πόσες μονάδες να προστεθούν;', '50');
    if (!amountStr) return;
    const amount = parseInt(amountStr, 10);
    if (!Number.isFinite(amount) || amount === 0) {
      toast.error('Μη έγκυρος αριθμός');
      return;
    }
    setWorking(userId);
    try {
      await axios.patch(`${API}/admin/users/${userId}/credits`, { amount }, { withCredentials: true });
      toast.success(`Προστέθηκαν ${amount} μονάδες`);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Σφάλμα');
    } finally {
      setWorking(null);
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Διαγραφή χρήστη ${email}; Η ενέργεια δεν αναιρείται.`)) return;
    setWorking(userId);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success('Διαγράφηκε');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Σφάλμα');
    } finally {
      setWorking(null);
    }
  };

  const setBeta = async (userId) => {
    const days = window.prompt('Διάρκεια beta (ημέρες);', '90');
    if (!days) return;
    setWorking(userId);
    try {
      await axios.post(`${API}/admin/beta/set/${userId}`, { duration_days: parseInt(days, 10) }, { withCredentials: true });
      toast.success('Έγινε beta tester');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Σφάλμα');
    } finally {
      setWorking(null);
    }
  };

  if (notAdmin) {
    return (
      <div className="min-h-screen bg-[#f7f2e7] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow p-8 max-w-md text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Δεν έχεις πρόσβαση</h1>
          <p className="text-slate-600 mb-6">Αυτή η περιοχή είναι μόνο για διαχειριστές.</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-red-700 hover:bg-red-800 text-white">
            Επιστροφή στο Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f2e7]">
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-stone-100 rounded-lg" data-testid="admin-back-btn">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900">Admin Panel</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Διαχειριστής</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
            <StatCard icon={<Users className="w-5 h-5" />} label="Χρήστες" value={stats.total_users} />
            <StatCard icon={<Activity className="w-5 h-5" />} label="Unlimited" value={stats.unlimited_users} accent="text-green-700" />
            <StatCard icon={<Euro className="w-5 h-5" />} label="MRR" value={`€${stats.mrr_eur}`} accent="text-red-700" />
            <StatCard icon={<Euro className="w-5 h-5" />} label="ARR" value={`€${stats.arr_eur}`} accent="text-red-700" />
          </div>
        )}

        {/* Οικονομική εικόνα */}
        <FinancePanel />

        {/* Χρήση ανά χρήστη */}
        <UsagePanel />

        {/* Beta tester management */}
        <BetaPanel onChange={loadAll} />

        {/* myDATA settings */}
        <MyDataSettings />

        {/* Email settings + compose */}
        <EmailSettings users={users} />

        {/* Users table */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Χρήστες ({users.length})</h2>
            <Button size="sm" variant="outline" onClick={loadAll} disabled={loading}>Ανανέωση</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-stone-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Ονομα</th>
                  <th className="px-3 py-2 text-left">Γραφειο</th>
                  <th className="px-3 py-2 text-center">Credits</th>
                  <th className="px-3 py-2 text-center">Plan</th>
                  <th className="px-3 py-2 text-center">Ληξη</th>
                  <th className="px-3 py-2 text-center">Ενεργειες</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {loading && users.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Φόρτωση...</td></tr>
                )}
                {!loading && users.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Κανένας χρήστης</td></tr>
                )}
                {users.map((u) => (
                  <tr key={u._id} className={working === u._id ? 'opacity-50' : ''}>
                    <td className="px-3 py-2 font-medium text-slate-900">{u.email}</td>
                    <td className="px-3 py-2 text-slate-700">{u.name || '—'}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{u.office_name || '—'}</td>
                    <td className="px-3 py-2 text-center font-mono font-medium">{u.credits ?? 0}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                        u.subscription_plan === 'unlimited' ? 'bg-green-100 text-green-800' :
                        u.subscription_plan === 'beta' ? 'bg-amber-100 text-amber-800' :
                        u.subscription_plan === 'limited' ? 'bg-amber-100 text-amber-700' :
                        'bg-stone-100 text-slate-600'
                      }`}>
                        {PLAN_LABEL[u.subscription_plan] || 'Δωρεάν'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-slate-600">{fmtDate(u.subscription_end)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          title="Καρτέλα χρήστη"
                          onClick={() => setOverviewUserId(u._id)}
                          className="p-1 hover:bg-stone-100 rounded"
                          data-testid={`admin-user-overview-${u._id}`}
                        >
                          <Eye className="w-4 h-4 text-slate-600" />
                        </button>
                        <button
                          title="Beta Tester (απεριόριστο)"
                          onClick={() => setBeta(u._id)}
                          className="p-1 hover:bg-amber-50 rounded"
                          data-testid={`admin-set-beta-${u._id}`}
                        >
                          <Sparkles className="w-4 h-4 text-amber-700" />
                        </button>
                        <button
                          title="Unlimited 1 μήνα"
                          onClick={() => activatePlan(u._id, 'unlimited', 1)}
                          className="p-1 hover:bg-green-50 rounded"
                          data-testid={`admin-activate-unlimited-${u._id}`}
                        >
                          <Check className="w-4 h-4 text-green-700" />
                        </button>
                        <button
                          title="Προσθήκη credits"
                          onClick={() => addCredits(u._id)}
                          className="p-1 hover:bg-amber-50 rounded"
                          data-testid={`admin-add-credits-${u._id}`}
                        >
                          <Plus className="w-4 h-4 text-red-700" />
                        </button>
                        {!u.is_admin && (
                          <button
                            title="Διαγραφή"
                            onClick={() => deleteUser(u._id, u.email)}
                            className="p-1 hover:bg-red-50 rounded"
                            data-testid={`admin-delete-user-${u._id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-700" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
      <UserOverviewModal userId={overviewUserId} onClose={() => setOverviewUserId(null)} />
    </div>
  );
}

const StatCard = ({ icon, label, value, accent }) => (
  <div className="bg-white rounded-xl border border-stone-200 p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center text-slate-600">{icon}</div>
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-xl font-semibold ${accent || 'text-slate-900'}`}>{value}</div>
    </div>
  </div>
);

function BetaPanel({ onChange }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [duration, setDuration] = useState(90);
  const [working, setWorking] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/beta/stats`, { withCredentials: true });
      setStats(r.data);
    } catch (err) {
      // silent — non-admins shouldn't render this anyway
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const bulkConvert = async (dry) => {
    if (!dry && !window.confirm(`Σίγουρα; Όλοι οι free χρήστες θα γίνουν Beta Testers για ${duration} ημέρες.`)) return;
    setWorking(true);
    try {
      const r = await axios.post(`${API}/admin/beta/bulk-convert`, { dry_run: dry, duration_days: duration }, { withCredentials: true });
      if (dry) {
        toast.info(`Θα μετατραπούν ${r.data.candidates} χρήστες`);
      } else {
        toast.success(`Έγιναν ${r.data.modified} beta testers (έως ${new Date(r.data.subscription_end).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' })})`);
      }
      await load();
      if (!dry) onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Σφάλμα');
    } finally {
      setWorking(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 p-4 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Φόρτωση beta panel...
      </div>
    );
  }

  return (
    <div className="bg-amber-50/50 border border-amber-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-amber-200 flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-amber-700" />
        <h2 className="text-sm font-semibold text-slate-800">Διαχείριση Beta Testers</h2>
      </div>
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded border border-stone-200 px-3 py-2">
            <div className="text-xs text-slate-500">Free</div>
            <div className="text-xl font-bold text-slate-900">{stats.by_plan?.free || 0}</div>
          </div>
          <div className="bg-white rounded border border-stone-200 px-3 py-2">
            <div className="text-xs text-slate-500">Beta (active)</div>
            <div className="text-xl font-bold text-amber-700">{stats.active_beta}</div>
          </div>
          <div className="bg-white rounded border border-stone-200 px-3 py-2">
            <div className="text-xs text-slate-500">Beta (expired)</div>
            <div className="text-xl font-bold text-slate-500">{stats.expired_beta}</div>
          </div>
          <div className="bg-white rounded border border-stone-200 px-3 py-2">
            <div className="text-xs text-slate-500">Unlimited</div>
            <div className="text-xl font-bold text-green-700">{stats.by_plan?.unlimited || 0}</div>
          </div>
        </div>

        <div className="bg-white rounded border border-amber-200 px-4 py-3">
          <div className="text-sm font-semibold text-slate-800 mb-2">Μετατροπή όλων των Free → Beta</div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-slate-600">Διάρκεια:</label>
            <input
              type="number"
              min={1}
              max={365}
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value || '1', 10))}
              className="w-20 h-9 border border-stone-300 rounded px-2 text-sm"
              data-testid="beta-duration-input"
            />
            <span className="text-xs text-slate-600">ημέρες</span>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="outline"
              disabled={working}
              onClick={() => bulkConvert(true)}
              data-testid="beta-dryrun-btn"
            >
              {working ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Προεπισκόπηση'}
            </Button>
            <Button
              size="sm"
              disabled={working}
              onClick={() => bulkConvert(false)}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="beta-convert-btn"
            >
              {working ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Μετατροπή Όλων'}
            </Button>
          </div>
        </div>

        {stats.recent_beta_testers?.length > 0 && (
          <div className="text-xs text-slate-600">
            <div className="font-medium mb-1">Πρόσφατοι Beta Testers:</div>
            <div className="flex flex-wrap gap-1.5">
              {stats.recent_beta_testers.map((b) => (
                <span
                  key={b.id}
                  className={`px-2 py-0.5 rounded ${b.active ? 'bg-amber-100 text-amber-800' : 'bg-stone-100 text-slate-500'}`}
                >
                  {b.name || b.email}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
